import React, { Fragment, FunctionComponent, useEffect, useState } from "react";
import styles from "./Footer1.module.scss";
import Footer1Props from "./Footer1Props.interface";
import SocialLinks from "./components/SocialLinks/SocialLinks";
import setFooterStyles from "./hooks/setFooterStyles";
import Anchor from "components/Anchor/Anchor";
import Paragraph from "components/Paragraph/Paragraph";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import { useSelector } from "react-redux";

const Footer1: FunctionComponent<Footer1Props> = (props) => {
    const siteSettings = useSelector(state => state.settings.content);
    let footer: any;
    let settings: any;
    if (props?.theme?.footer && props?.theme?.footer.length > 0) {
        footer = props?.theme?.footer?.[0]?.content;
        settings = props?.theme?.footer?.[0]?.settings;
    }
    const openingHours = useSelector(state => state.settings.openingHours);
    const certificationIcon = props?.certificationIcon?.toLowerCase().replace(/ /g, "-");
    const footerStyles = setFooterStyles(footer);
    const hasToolBarClass = (props.toolbarMenu && props.toolbarMenu.length > 0) || (props.toolbarButtons && props.toolbarButtons.length > 0) ? styles.hasToolbar : undefined;
    const headlineCaseClass = settings?.headlineCase ? styles[`case_${settings?.headlineCase}`] : undefined;


    // If there is heading buttons, we want to add a small margin-bottom, so there is space
    // for the fixed header buttons
    const hasHeaderButtonsClass = siteSettings && siteSettings?.headerButtons.length > 0 ? styles.has_header_buttons : "";

    const fetchSuccess = () => !!openingHours;
    const canRenderDynamicOpeningHours = () => {
        return (props.openingHoursFetchFromAPI === true && fetchSuccess())
    }
    const canRenderStaticOpeningHours = () => {
        return ((props.openingHoursFetchFromAPI === true && !fetchSuccess()) || props.openingHoursFetchFromAPI === false) && (!!props.openingHoursHeading && !!props.openingHours)
    }
    const canRenderOpeningHours = () => {
        return (canRenderDynamicOpeningHours() || canRenderStaticOpeningHours())
    }

    const renderColumn = (item: any): null | JSX.Element => {
        switch (item.content._contentType) {
            case "logoDescription": {
                return (
                    <div key={item.contentUdi} className={styles.item}>
                        {/* Logo */}
                        {item.content.logo && (
                            <ImageViewer className={styles.logo} src={item.content.logo.umbracoFile.src} extensionType="png" type="contain" aspectRatioX={item.content.logo.umbracoWidth} aspectRatioY={item.content.logo.umbracoHeight} />
                        )}

                        {/* Heading */}
                        {props.descriptionHeading && (<DynamicHeading className={combineClasses(styles.heading, headlineCaseClass)} headlineSize={settings?.headlineSize} headlineWeight={settings?.headlineWeight} factor={0.5}>{props.descriptionHeading}</DynamicHeading>)}

                        {/* Description */}
                        {props.description && (
                            <Paragraph innerHTML={props.description} />
                        )}
                    </div>
                );
            }
            case "address": {
                return (
                    <div key={item.contentUdi} className={combineClasses(styles.item, styles.address)}>
                        {/* Heading */}
                        {props.addressHeading && (
                            <DynamicHeading
                                className={combineClasses(styles.heading, headlineCaseClass)}
                                headlineSize={settings?.headlineSize}
                                headlineWeight={settings?.headlineWeight} factor={0.5}>{props.addressHeading}</DynamicHeading>
                        )}

                        {/* Address */}
                        {props.address && (
                            <Paragraph innerHTML={props.address} />
                        )}

                        {/* Heading */}
                        {props.contactHeading && (
                            <DynamicHeading className={combineClasses(styles.heading, headlineCaseClass)} headlineSize={settings?.headlineSize} headlineWeight={settings?.headlineWeight} factor={0.5}>{props.contactHeading}</DynamicHeading>
                        )}

                        {/* Contact */}
                        {props.contact && (
                            <Paragraph innerHTML={props.contact} />
                        )}
                    </div>
                );
            }
            case "program": {
                return (
                    canRenderOpeningHours() ?
                        <div key={item.contentUdi} className={styles.item}>
                            {/* Opening Hours: Dynamic */}
                            {canRenderDynamicOpeningHours() ? (
                                <Fragment>
                                    {/* Opening Hours Heading */}
                                    {openingHours?.weeklyOpeningHoursTitleText && openingHours?.weekDays?.[0] && (
                                        <DynamicHeading className={combineClasses(styles.heading, headlineCaseClass)} headlineSize={settings?.headlineSize} headlineWeight={settings?.headlineWeight} factor={0.5}>{openingHours?.weeklyOpeningHoursTitleText}</DynamicHeading>
                                    )}

                                    {/* Opening Hours */}
                                    {openingHours?.weekDays?.[0] && (
                                        <ul className={styles.opening_hours_list}>
                                            {openingHours?.weekDays.map((openingHour: any, index: number) => (
                                                <li className={styles.opening_hours_item} key={index}>{openingHour}</li>
                                            ))}
                                        </ul>
                                    )}

                                    {/* Special Opening Hours Heading */}
                                    {openingHours?.specialOpeningHoursTitleText && openingHours?.specialDays?.[0] && (
                                        <DynamicHeading className={combineClasses(styles.heading, headlineCaseClass)} headlineSize={settings?.headlineSize} headlineWeight={settings?.headlineWeight} factor={0.5}>{openingHours?.specialOpeningHoursTitleText}</DynamicHeading>
                                    )}

                                    {/* Special Opening Hours */}
                                    {openingHours?.specialDays?.[0] && (
                                        <ul className={styles.opening_hours_list}>
                                            {openingHours?.specialDays.map((entry: any, index: number) => (
                                                <li className={styles.opening_hours_item} key={index}>{entry}</li>
                                            ))}
                                        </ul>
                                    )}
                                </Fragment>
                            ) : null}

                            {/* Opening Hours: Static */}
                            {canRenderStaticOpeningHours() ? (
                                <Fragment>
                                    {/* Heading */}
                                    {props.openingHoursHeading && (
                                        <DynamicHeading className={combineClasses(styles.heading, headlineCaseClass)} headlineSize={settings?.headlineSize} headlineWeight={settings?.headlineWeight} factor={0.5}>{props.openingHoursHeading}</DynamicHeading>
                                    )}

                                    {/* Heading */}
                                    {props.openingHours && (
                                        <Paragraph innerHTML={props.openingHours} />
                                    )}
                                </Fragment>
                            ) : null}
                        </div>
                        : null
                );
            }
            case "ageLimitsSocialLinks": {
                return (
                    <div key={item.contentUdi} className={combineClasses(styles.item, styles.age_limit_social_links)}>
                        {/* Heading */}
                        {props.ageLimitsHeading && (
                            <DynamicHeading className={combineClasses(styles.heading, headlineCaseClass)} headlineSize={settings?.headlineSize} headlineWeight={settings?.headlineWeight} factor={0.5}>{props.ageLimitsHeading}</DynamicHeading>
                        )}

                        {/* Age Limits */}
                        {props.ageLimits && (
                            <Paragraph className={styles.text} innerHTML={props.ageLimits} />
                        )}

                        <SocialLinks {...props} />
                    </div>
                );
            }
            default: {
                return null;
            }
        }
    };

    return (
        <footer className={combineClasses(styles.wrapper, hasHeaderButtonsClass)} style={footerStyles}>
            {/* Top */}
            <div className={styles.top_wrapper}>
                <div className={styles.top}>
                    {footer?.footerBlocks && footer.footerBlocks.map((item: any) => {
                        return renderColumn(item);
                    })}
                </div>
            </div>

            {/* Bottom */}
            <div className={combineClasses(styles.bottom_wrapper, hasToolBarClass)}>
                <div className={styles.bottom}>
                    <div className={styles.copyright}>&copy; {new Date().getFullYear()} Copyright {props.theme?.siteName}</div>

                    <div className={styles.links_wrapper}>
                        {props.certificationIcon && props.certificationLink ? (
                            <Anchor className={styles.certification} to={props.certificationLink?.[0].content.link}>
                                <IconViewer className={styles.certification_icon} name={certificationIcon} />
                                <p>{props.certificationLink?.[0].content.label}</p>
                            </Anchor>
                        ) : null}

                        {props.footerLinks && props.footerLinks.length > 0 ? (
                            <nav>
                                {props.footerLinks?.map((footerLink: any, index: number) => {
                                    return (
                                        <Anchor key={index} className={styles.link} to={footerLink.content.link}>
                                            {footerLink.content.label}
                                        </Anchor>
                                    );
                                })}
                            </nav>
                        ) : null}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer1;